import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Menu, Plus, Send, X } from "lucide-react";
import { lookupUser, sendMail, type MailReward } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { useDirtyStore } from "@/lib/dirty-store";
import { HERO_NAMES, REWARD_TYPE_NAMES } from "@/types/cloud-data";
import { IAP_CATALOG, IAP_REWARD_TYPE } from "@/lib/iap-rewards";
import { ItemPicker } from "@/components/ItemPicker";
import { initials } from "@/components/players/player-presenters";
import { RewardIcon } from "@/components/players/PlayerInspector";
import { ProductArt, productName } from "@/components/purchases/ProductArt";
import { WorkspaceNavigation } from "@/components/workspace/WorkspaceNavigation";
import { captureMail, createSubmitLock } from "@/components/player-actions/action-model";
import "@/components/players/players-workspace.css";
import "./send-mail.css";

const fields: Record<number, { amount?: boolean; item?: boolean; hero?: boolean; buff?: boolean; recipe?: boolean; iap?: boolean }> = {
  0: { amount: true, item: true }, 1: { amount: true }, 2: { amount: true },
  4: { hero: true }, 10: { amount: true, buff: true }, 11: { amount: true },
  12: { amount: true }, 14: { amount: true }, 15: { amount: true }, 16: { recipe: true }, 100: { amount: true, iap: true },
};
const defaultReward: MailReward = { rewardType: 2, amount: 100 };
// Some store products share a readable name; those show their ID too.
const iapNames = IAP_CATALOG.map(p => productName(p.id));
const iapLabel = (id: string, i: number) => iapNames.indexOf(iapNames[i]) !== iapNames.lastIndexOf(iapNames[i]) ? `${iapNames[i]} · ${id}` : iapNames[i];
const findIap = (id?: string) => IAP_CATALOG.find(p => p.id === id || p.aliases.includes(id ?? ""));

/** Why a reward can't be sent yet, or null when it is complete. */
function rewardProblem(reward: MailReward) {
  const f = fields[reward.rewardType] ?? {};
  if (f.item && !reward.itemID) return "Pick an item";
  if (f.hero && reward.hero == null) return "Pick a hero";
  if (f.buff && !reward.developerBuffID?.trim()) return "Enter a buff ID";
  if (f.iap) { const product = findIap(reward.iapProductId); if (!product) return "Pick a product"; if (product.unavailable) return product.unavailable; }
  if (f.amount && !(reward.amount > 0)) return "Amount must be above 0";
  return null;
}

function RewardRow({ reward, index, onChange, onRemove }: { reward: MailReward; index: number; onChange: (patch: Partial<MailReward>) => void; onRemove: () => void }) {
  const f = fields[reward.rewardType] ?? {};
  const problem = rewardProblem(reward);
  const n = index + 1;
  return <li className="sm-reward">
    <span className="sm-reward-icon">{reward.rewardType === IAP_REWARD_TYPE && reward.iapProductId ? <ProductArt productId={reward.iapProductId} className="sm-art" /> : <RewardIcon reward={reward} />}</span>
    <select className="sm-type" aria-label={`Reward ${n} type`} value={reward.rewardType} onChange={e => onChange({
      rewardType: Number(e.target.value), amount: 1, itemID: undefined, hero: undefined, developerBuffID: undefined, recipe: undefined, iapProductId: undefined })}>
      {Object.entries(REWARD_TYPE_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
    <span className="sm-detail">
      {f.item && <ItemPicker className="sm-item-picker" value={reward.itemID ?? ""} onChange={itemID => onChange({ itemID })} placeholder="Search items" />}
      {f.hero && <select aria-label={`Reward ${n} hero`} value={reward.hero ?? ""} onChange={e => onChange({ hero: Number(e.target.value) })}>
        <option value="" disabled>Hero</option>
        {Object.entries(HERO_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>}
      {f.buff && <input aria-label={`Reward ${n} buff ID`} placeholder="Buff ID" value={reward.developerBuffID ?? ""} onChange={e => onChange({ developerBuffID: e.target.value })} />}
      {f.recipe && <input aria-label={`Reward ${n} recipe ID`} placeholder="Recipe ID" type="number" value={reward.recipe ?? ""} onChange={e => onChange({ recipe: parseInt(e.target.value) || 0 })} />}
      {f.iap && <select aria-label={`Reward ${n} product`} value={reward.iapProductId ?? ""} title={findIap(reward.iapProductId)?.summary} onChange={e => onChange({ iapProductId: e.target.value })}>
        <option value="" disabled>Product</option>
        {IAP_CATALOG.map((p, i) => <option key={p.id} value={p.id} disabled={!!p.unavailable}>{iapLabel(p.id, i)}{p.unavailable ? " — unavailable" : ""}</option>)}
      </select>}
    </span>
    {f.amount ? <input className="sm-amount" aria-label={`Reward ${n} ${f.iap ? "packs" : "amount"}`} type="number" min={1} max={f.iap ? 100 : undefined}
      value={reward.amount} onChange={e => onChange({ amount: parseInt(e.target.value) || 0 })} /> : <span className="sm-amount-spacer" />}
    <button type="button" className="pw-icon-button sm-remove" aria-label={`Remove reward ${n}`} onClick={onRemove}><X size={15} /></button>
    {problem && <span className="sm-problem">{problem}</span>}
  </li>;
}

export function SendMailWorkspace() {
  const id = useParams().playfabId!;
  const qc = useQueryClient();
  const me = useAuthStore(s => s.me);
  const navDialog = useRef<HTMLDialogElement>(null);
  const [readOnly, setReadOnly] = useState(import.meta.env.VITE_DASHBOARD_PREVIEW === "true");
  useEffect(() => {
    if (import.meta.env.DEV || import.meta.env.VITE_DASHBOARD_PREVIEW === "true") void fetch("/__fixtures").then(async r => {
      if (r.ok && r.headers.get("content-type")?.includes("application/json")) { const body = await r.json() as { mode?: string; readOnly?: boolean }; setReadOnly(body.mode === "snapshot" || body.readOnly === true); }
    }).catch(() => {});
  }, []);

  const account = useQuery({ queryKey: ["pf-account", id], queryFn: () => lookupUser("playfabId", id) });
  const name = account.data?.TitleInfo?.DisplayName || account.data?.Username || "Unnamed player";
  const back = `/accounts?player=${encodeURIComponent(id)}`;

  const [subject, setSubject] = useState("From the team");
  const [body, setBody] = useState("");
  const [rewards, setRewards] = useState<MailReward[]>([{ ...defaultReward }]);
  const [changed, setChanged] = useState(false);
  const [sent, setSent] = useState(false);
  const setDirty = useDirtyStore(s => s.setDirty);
  useEffect(() => { setDirty(changed); return () => setDirty(false); }, [changed, setDirty]);

  const submitting = useRef(createSubmitLock());
  const mutation = useMutation({
    mutationFn: (payload: ReturnType<typeof captureMail>) => sendMail(payload.id, payload.rewards, payload.subject, payload.body, payload.name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["player-mail", id] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      setSent(true); setBody(""); setRewards([{ ...defaultReward }]); setChanged(false);
    },
    onSettled: () => { submitting.current.end(); },
  });

  function edit<T>(apply: (value: T) => void) { return (value: T) => { apply(value); setChanged(true); setSent(false); mutation.reset(); }; }
  const updateReward = (i: number) => edit<Partial<MailReward>>(patch => setRewards(current => current.map((reward, j) => j === i ? { ...reward, ...patch } : reward)));
  const viewOnly = !!me?.role && me.role !== "internal";
  const blocked = !subject.trim() || !rewards.length || rewards.some(rewardProblem) || mutation.isPending || readOnly || viewOnly;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (blocked || !submitting.current.begin()) return;
    mutation.mutate(captureMail({ id, displayName: account.data ? name : null }, rewards, subject.trim(), body));
  }
  function leave(event: React.MouseEvent) { if (!useDirtyStore.getState().confirmLeave()) event.preventDefault(); }

  return <div className="players-workspace sm-workspace">
    <aside className="pw-sidebar"><WorkspaceNavigation /></aside>
    <dialog ref={navDialog} className="pw-mobile-nav" aria-label="Main navigation">
      <button className="pw-icon-button pw-nav-close" aria-label="Close navigation" onClick={() => navDialog.current?.close()}><X size={20} /></button>
      <WorkspaceNavigation onNavigate={() => navDialog.current?.close()} />
    </dialog>
    <main className="pw-main">
      <header className="pw-topbar">
        <button className="pw-icon-button pw-menu-button" aria-label="Open navigation" onClick={() => navDialog.current?.showModal()}><Menu size={21} /></button>
        <span className="pw-topbar-brand"><strong>EC2</strong> Admin</span>
        <span className="pw-breadcrumb"><Link to="/accounts" onClick={leave}>Players</Link><ChevronRight size={13} /><Link to={back} onClick={leave}>{name}</Link><ChevronRight size={13} /><strong>Send mail</strong></span>
        <div className="pw-topbar-right">{(readOnly || viewOnly) && <span className="pw-preview-label"><span />{readOnly ? "Preview" : "View only"} · Sending disabled</span>}<span className="pw-avatar pw-avatar-small">{initials(me?.globalName ?? me?.username)}</span></div>
      </header>
      <div className="sm-scroll">
        <form className="sm-page" onSubmit={submit}>
          <div className="sm-head">
            <Link to={back} onClick={leave} className="pw-icon-button" aria-label="Back to player"><ArrowLeft size={17} /></Link>
            <h1>Send mail</h1>
            <span className="sm-recipient" title={id}><span className="pw-avatar pw-avatar-small">{initials(name)}</span><span><strong>{account.isPending ? "Loading…" : name}</strong><small>{id}</small></span></span>
          </div>
          {account.isError && <p className="sm-alert" role="alert">{account.error.message}</p>}
          <fieldset className="sm-card" disabled={mutation.isPending}>
            <input className="sm-subject" aria-label="Subject" placeholder="Subject" value={subject} onChange={e => edit<string>(setSubject)(e.target.value)} />
            <textarea className="sm-body" aria-label="Message" placeholder="Message (optional)" rows={3} value={body} onChange={e => edit<string>(setBody)(e.target.value)} />
            <h2 className="sm-rewards-head">Rewards</h2>
            {rewards.length ? <ol className="sm-rewards">{rewards.map((reward, i) => <RewardRow key={i} index={i} reward={reward} onChange={updateReward(i)}
              onRemove={() => edit<number>(j => setRewards(current => current.filter((_, k) => k !== j)))(i)} />)}</ol>
              : <p className="sm-none">No rewards</p>}
            <button type="button" className="pw-button sm-add" onClick={() => edit<void>(() => setRewards(current => [...current, { ...defaultReward }]))()}><Plus size={14} />Add reward</button>
          </fieldset>
          {mutation.isError && <p className="sm-alert" role="alert">{mutation.error.message}. It may have reached the server — check the player’s mail before retrying.</p>}
          {sent && <p className="sm-alert is-ok" role="status">Sent to {name}. <Link to={`${back}&tab=mail`}>View mail</Link></p>}
          <div className="sm-actions">
            <Link to={back} onClick={leave} className="pw-button">Cancel</Link>
            <button type="submit" className="pw-button pw-button-primary" disabled={blocked}><Send size={14} />{mutation.isPending ? "Sending…" : "Send"}</button>
          </div>
        </form>
      </div>
    </main>
  </div>;
}
